import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowRight, FaCheckCircle, FaCog, FaAppleAlt, FaSeedling, FaHardHat, FaTruck, FaTshirt, FaFlask, FaWhatsapp } from 'react-icons/fa';

const ICON_MAP = { FaAppleAlt, FaSeedling, FaHardHat, FaTruck, FaTshirt, FaFlask, FaCog };

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whatsapp, setWhatsapp] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get(`/api/services/${slug}`)
      .then(r => { setService(r.data); setLoading(false); })
      .catch(() => { navigate('/hizmetler'); });
    axios.get('/api/settings').then(r => setWhatsapp(r.data.whatsapp_number || '')).catch(() => {});
  }, [slug, navigate]);

  if (loading) return (
    <div className="pt-20 min-h-screen flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!service) return null;

  const Icon = ICON_MAP[service.icon] || FaCog;
  const features = Array.isArray(service.features) ? service.features : [];

  return (
    <div className="pt-20">
      {/* Header */}
      <div className="hero-gradient hero-pattern py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-gray-400 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-secondary">Ana Sayfa</Link>
            <FaArrowRight size={10} className="text-gray-500" />
            <Link to="/hizmetler" className="hover:text-secondary">Hizmetler</Link>
            <FaArrowRight size={10} className="text-gray-500" />
            <span className="text-white">{service.name}</span>
          </nav>
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-secondary/20 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Icon className="text-secondary text-3xl" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">{service.name}</h1>
              {service.short_description && (
                <p className="text-gray-300 mt-2 max-w-2xl">{service.short_description}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {service.image_url && (
              <div className="rounded-2xl overflow-hidden mb-8">
                <img src={service.image_url} alt={service.name} className="w-full h-72 object-cover" />
              </div>
            )}

            {service.full_description && (
              <div className="prose max-w-none">
                <h2 className="text-2xl font-bold text-primary mb-4">Hizmet Detayı</h2>
                <div className="text-gray-600 leading-relaxed whitespace-pre-line">{service.full_description}</div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Features */}
            {features.length > 0 && (
              <div className="card p-6">
                <h3 className="font-semibold text-primary text-lg mb-4">Öne Çıkan Özellikler</h3>
                <ul className="space-y-3">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <FaCheckCircle className="text-secondary mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Contact CTA */}
            <div className="bg-primary rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-secondary/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon className="text-secondary text-2xl" />
              </div>
              <h3 className="text-white font-semibold mb-2">Bu Hizmet Hakkında Bilgi Alın</h3>
              <p className="text-gray-400 text-sm mb-5">Uzmanlarımız size en uygun çözümü sunmak için hazır.</p>
              <div className="space-y-3">
                <Link to="/iletisim" className="btn-primary w-full justify-center block text-center">
                  İletişime Geç
                </Link>
                {whatsapp && (
                  <a href={`https://wa.me/${whatsapp}?text=Merhaba, ${service.name} hizmeti hakkında bilgi almak istiyorum.`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-lg transition-colors w-full">
                    <FaWhatsapp size={18} /> WhatsApp
                  </a>
                )}
              </div>
            </div>

            {/* Other Services */}
            <div className="card p-5">
              <h3 className="font-semibold text-gray-700 mb-3">Diğer Hizmetler</h3>
              <Link to="/hizmetler" className="text-secondary text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all">
                Tüm hizmetleri gör <FaArrowRight size={11} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
